import Divider from "antd/lib/divider"
import Tabs from "antd/lib/tabs"
import React, { useEffect, useState } from "react"
import { connect, useDispatch } from "react-redux"
import { useNavigate, useSearchParams } from "react-router"
import { LocalAudioTrack, LocalVideoTrack } from "twilio-video"

import { actions as documentActions } from "client/documents/actions"
import { DocumentType } from "client/documents/types"
import opticActions from "client/optic/actions"
import { Button } from "components/Button"
import FatButton from "components/Button/FatButton/FatButton"
import { Buttons, Card } from "components/Card/Card"
import Loader from "components/Loaders/Loader/Loader"
import { GenericModal } from "components/Modal/Modal"
import PreviousButton from "components/Previous/PreviousButton"
import { Subtitle } from "components/Title/Title"
import { createUserLocalTracks } from "containers/Call/utils"
import { Diagnosis } from "containers/Diagnosis/Diagnosis"
import { logout } from "containers/Login/services/actions"
import Newsletter from "containers/Newsletter"
import paymentActions from "containers/Payment/services/actions"
import { History } from "containers/Profile/History/History"
import { AddCard, CardHistory } from "containers/Stripe"
import surveyActions from "containers/Survey/services/actions"
import { cdn } from "core/cdn"
import { SURVEY_PAGE } from "core/constants"
import { languages } from "lib/languages"
import { Customer, Prospect, Relative } from "types/entity"
import { Dispatcher, Message } from "types/redux"
import { ClientStore, ProfileStore } from "types/store"

import { PatientInformation } from "./PatientInformation"
import { SectionHeader } from "./SectionHeader"
import styles from "./Profile.module.scss"
import actions from "./services/actions"
import dayjs from "dayjs"
import { dateFromApi } from "lib/api-compliance"
import { ChangePasswordModal } from "components/Modal/ChangePasswordModal"
import { GET_CALL } from "containers/Call/services/constants"

const TAB_SEARCH_PARAM = "tab"

type InfoSection = "menu" | "personal"

// Tiles of the INFORMATIONS tab (same layout as kiosk)
const INFO_TILE_STYLE: React.CSSProperties = {
  minHeight: 280,
  paddingBottom: 10,
  justifyContent: "flex-start",
  paddingTop: 40,
  gap: 20,
}
const INFO_TILE_IMG_STYLE: React.CSSProperties = {
  width: 100,
  height: 100,
  flexGrow: 0,
  flexShrink: 0,
  objectFit: "contain",
}
const INFO_TILE_LABEL_STYLE: React.CSSProperties = {
  fontSize: 28,
  fontWeight: 400,
  color: "#211452",
  textAlign: "center",
  flexGrow: 0,
  flexShrink: 0,
  height: 80,
  width: 270,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
}

const Profile = (props: ProfileStore) => {
  const navigate = useNavigate()
  const [logoutModalVisible, setLogoutModalVisible] = useState(false)
  const [loading, setLoading] = useState(false)
  const [diagnosis, setDiagnosis] = useState(false)
  const [shouldResetPassword, setShouldResetPassword] = useState(false)
  const [infoSection, setInfoSection] = useState<InfoSection>("menu")
  const welcomeTitle = `${languages.welcome}, ${props.patient.firstname}`
  const dispatch = useDispatch()
  const [TLC, INFORMATIONS, HISTORY, PAIEMENT, OTHERS] = [
    "TLC",
    "INFORMATIONS",
    "HISTORY",
    "PAIEMENT",
    "OTHERS",
  ]
  const TAB_KEYS = [TLC, INFORMATIONS, HISTORY, PAIEMENT, OTHERS]
  // The active tab is kept in the URL (?tab=) so a page refresh brings the
  // patient back on the same tab instead of the default one (OPHTA-480)
  const [searchParams, setSearchParams] = useSearchParams()
  const tabFromUrl = searchParams.get(TAB_SEARCH_PARAM)
  const activeTabKey = TAB_KEYS.includes(tabFromUrl) ? tabFromUrl : TLC
  const setActiveTabKey = (key: string) => {
    const params = new URLSearchParams(searchParams)
    params.set(TAB_SEARCH_PARAM, key)
    setSearchParams(params, { replace: true })
  }

  useEffect(() => {
    const now = dayjs()
    const daysDiff = (props.patient.password_expiration_date).diff(now, "days")
    if (daysDiff <= 7) {
      // Propose le changement de mot de passe
      setShouldResetPassword(true)
    }

  }, [])

  const onFinish = () => {
    dispatch(surveyActions.reset())
    dispatch(paymentActions.reset())
    dispatch(opticActions.checkPath(true))
    props.resetProfile && props.resetProfile()
    navigate(SURVEY_PAGE)
  }
  const startDiagnostic = () => {
    setLoading(true)
    checkLocalDevices()
      .then(() => onFinish())
      .catch(() => setDiagnosis(true))
      .finally(() => {
        setLoading(false)
      })
  }

  const checkLocalDevices = () => {
    setLoading(true)
    return Promise.race([
      createUserLocalTracks().then((e) => {
        e.forEach(
          (track: LocalVideoTrack | LocalAudioTrack) =>
            track.stop && track.stop()
        )
        return e
      }),
      new Promise((_, rej) => setTimeout(() => rej(), 3000)),
    ])
  }

  if (shouldResetPassword)
    return <ChangePasswordModal
      patient={props.patient}
      onCancel={() => setShouldResetPassword(false)}
      onFinish={() => {
        setShouldResetPassword(false)
        dispatch({ type: GET_CALL })
      }} />

  if (diagnosis)
    return <Diagnosis onStop={() => setDiagnosis(false)} onFinish={onFinish} />
  return (
    <Card subtitle={welcomeTitle} message={props.message}>
      <PreviousButton
        danger
        text={languages.logout}
        onClick={() => setLogoutModalVisible(true)}
      />
      <Tabs
        className={styles.ProfileTabs}
        activeKey={activeTabKey}
        onChange={(key: string) => {
          if (key === HISTORY && !props.calls) props.getHistory()
          setActiveTabKey(key)
        }}
      >
        {/* TLC */}
        <Tabs.TabPane
          className={styles.Scroller}
          tab={<Subtitle text={languages.launch_tlc} />}
          key={TLC}
        >
          <Buttons direction="vertical">
            <img src={cdn("images/medecin_tlc_illustration.svg")} alt="" />
            <Divider />
            <Button
              type="primary"
              disabled={loading}
              loading={loading}
              onClick={startDiagnostic}
            >
              {languages.launch_tlc}
            </Button>
          </Buttons>
        </Tabs.TabPane>
        {/* INFORMATIONS */}
        <Tabs.TabPane
          className={styles.Scroller}
          tab={<Subtitle text={languages.profile_myInformations} />}
          key={INFORMATIONS}
        >
          <div style={{ display: "flex", flexWrap: "wrap", justifyContent: "center", gap: 60, marginTop: 50 }}>
            <FatButton
              img={cdn("images/pico_piece_administrative.svg")}
              imgStyle={INFO_TILE_IMG_STYLE}
              style={INFO_TILE_STYLE}
              onClick={() => setInfoSection("personal")}
            >
              <span style={INFO_TILE_LABEL_STYLE}>
                {languages.personalInformationsSubtitle}
              </span>
            </FatButton>
          </div>
        </Tabs.TabPane>
        {/* HISTORY */}
        <Tabs.TabPane
          className={styles.Scroller}
          tab={<Subtitle text={languages.profile_myHistory} />}
          key={HISTORY}
        >
          {props.calls && <History calls={props.calls} />}
          {props.loading && <Loader />}
        </Tabs.TabPane>

        {/* PAIEMENT */}
        <Tabs.TabPane
          className={styles.Scroller}
          tab={<Subtitle text={languages.profile_myPayments} />}
          key={PAIEMENT}
        >
          <CardHistory />
          <AddCard />
        </Tabs.TabPane>
        {/* NEWSLETTER */}
        <Tabs.TabPane
          className={styles.Scroller}
          tab={<Subtitle text={languages.profile_mySettings} />}
          key={OTHERS}
        >
          <Newsletter onNavigateToInformations={() => setActiveTabKey(INFORMATIONS)} active={activeTabKey === OTHERS} />
        </Tabs.TabPane>
      </Tabs>
      <GenericModal
        visible={logoutModalVisible}
        title={languages.logoutTitle}
        acceptText={languages.logout}
        onCancel={() => setLogoutModalVisible(false)}
        onAccept={() => {
          props.logout()
        }}
      />
      <GenericModal visible={infoSection === "personal"} noFooter width={1650} bodyStyle={{ height: 900, overflowY: "auto" }}>
        <div>
          <SectionHeader onBack={() => setInfoSection("menu")} title={languages.personalInformationsSubtitle} />
          <PatientInformation
            update_requires_action={props.update_requires_action}
            loading={props.loading}
            patient={props.patient}
            relatives={props.relatives}
            change={props.change}
            changeRelative={props.changeRelative}
          />
        </div>
      </GenericModal>
    </Card>
  )
}

const mapStateToProps = ({
  profile,
  client,
  stripe,
}: {
  profile: ProfileStore
  client: ClientStore
  stripe: { message?: Message }
}): ProfileStore => {
  const stripeMessage = stripe.message
  return {
    ...profile,
    patient: client.customer as Customer,
    relatives: client.relatives as Relative[],
    message: stripeMessage || profile.message,
  }
}
const mapDispatchToProps = (dispatch: Dispatcher): Partial<ProfileStore> => ({
  change: (p: Partial<Prospect>) => dispatch(actions.changePatientRequest(p)),
  changeRelative: (r: Partial<Relative>) =>
    dispatch(actions.changeRelativeRequest(r)),
  getHistory: () => dispatch(actions.historyRequest()),
  logout: () => dispatch(logout()),
  getDocuments: (id: string, d?: DocumentType[]) =>
    dispatch(documentActions.request(id, d)),
  resetProfile: () => dispatch(actions.reset()),

})

export default connect(mapStateToProps, mapDispatchToProps)(Profile)
